import React from 'react';
import { connect } from 'react-redux';
import has from 'lodash/has';
import get from 'lodash/get';
import isNil from 'lodash/isNil';

import { probeGetFile, probeClearFiles } from '../../../actions';
import DateUtils from '../../../lib/DateUtils';
import { bytesToString } from 'js/lib/FormatUtils';

import './FilePreview.scss';
import LoadableMonacoEditor from '../editors/monaco/LoadableMonacoEditor';
import { getEditorLanguage } from '../editors/monaco/MonacoEditorUtils';

function PreviewDialogContent({ fileNode, isLoading, response, mode }) {
  const fileName = fileNode && get(fileNode, 'displayPath', fileNode.name);
  const editorLanguage = getEditorLanguage(mode);

  return (
    <div className="file-modal">
      <div className="file-modal-body">
        <div className="preview-header">
          <div className="file-modal-title">
            <div className="title-container">
              File:{' '}
              <div data-testid="preview-file-name" className="file-name">
                {fileName}
              </div>
            </div>
            {fileNode && (has(fileNode, 'updatedAt') || has(fileNode, 'size')) && (
              <div className="treeview-list-description">
                {fileNode.updatedAt && (
                  <span>
                    <b>Modified:</b>{' '}
                    {DateUtils.convertDateStringToFormat(
                      fileNode.updatedAt,
                      'MMM D, YYYY [at] h:mma z',
                      true,
                      true,
                    )}{' '}
                    •{' '}
                  </span>
                )}
                <b>Size: </b>
                {!isNil(fileNode.size) ? bytesToString(fileNode.size) : '0 B'}
              </div>
            )}
          </div>
        </div>
        {isLoading ? null : (
          <div className="file-preview-editor-container">
            <LoadableMonacoEditor
              theme="dark"
              value={response}
              language={editorLanguage}
              width="200px"
              height="300px"
              options={{
                readOnly: true,
                minimap: { enabled: false },
              }}
              style={{
                padding: 0,
                backgroundColor: 'var(--color-greyscale-800)',
              }}
              className="file-preview-editor"
            />
          </div>
        )}
      </div>
    </div>
  );
}

class FilePreview extends React.Component {
  shouldComponentUpdate(nextProps) {
    let oldData = this.props.data || {};
    let newData = nextProps.data || {};
    let oldFileNode = this.props.fileNode || {};
    let newFileNode = nextProps.fileNode || {};
    let oldIsLoading = this.props.isLoading;
    let newIsLoading = nextProps.isLoading;
    return (
      oldIsLoading !== newIsLoading ||
      oldData.lastUpdatedAt !== newData.lastUpdatedAt ||
      oldFileNode.id !== newFileNode.id
    );
  }

  componentDidMount() {
    if (this.props.fileNode && this.props.selectedCredentialId && this.props.selectedConnection) {
      this.props.probeGetFile(
        this.props.selectedCredentialId,
        this.props.fileNode,
        this.props.selectedConnection,
        this.props.shouldFetchOnResource,
        this.props.resourceType,
        this.props.resourceId,
      );
    }
  }

  componentDidUpdate(prevProps) {
    let oldFileNode = prevProps.fileNode || {};
    let newFileNode = this.props.fileNode || {};
    if (newFileNode.id !== oldFileNode.id && this.props.fileNode) {
      this.props.probeGetFile(
        this.props.selectedCredentialId,
        this.props.fileNode,
        this.props.selectedConnection,
        this.props.shouldFetchOnResource,
        this.props.resourceType,
        this.props.resourceId,
      );
    }
  }
  componentWillUnmount() {
    this.props.probeClearFiles(
      this.props.selectedCredentialId,
      this.props.shouldFetchOnResource,
      this.props.resourceType,
      this.props.resourceId,
    );
  }
  render() {
    let {
      open,
      onClose,
      fileNode,
      isLoading,
      data: { statusCode, response, contentType, errorMessage },
    } = this.props;

    let mode = 'text';
    let height = window.innerHeight - 150;
    let isErrorMode = false;
    if (!isLoading) {
      if (contentType === 'application/json') {
        // contentType = { name: 'javascript', json: true };
        mode = 'json';
      }
      if (contentType === 'application/binary') {
        response =
          'This file contains binary data. File preview is not supported for binary data files.';
        isErrorMode = true;
      }
      if (statusCode >= 400) {
        response = `There was an error in opening the file.\nReason: ${errorMessage}`;
        isErrorMode = true;
      }
      if (!response) {
        response = 'This is an empty file.';
        isErrorMode = true;
      }
    }

    return (
      <PreviewDialogContent
        isLoading={isLoading}
        onClose={onClose}
        fileNode={fileNode}
        response={response}
        mode={mode}
        height={height}
        isErrorMode={isErrorMode}
      />
    );
  }
}

FilePreview.defaultProps = {
  shouldFetchOnResource: false,
  resourceId: null,
  resourceType: null,
};

const mapStateToProps = (state, ownProps) => {
  let { probe, request } = state;
  let { selectedCredentialId, fileNode, resourceId, shouldFetchOnResource, resourceType } =
    ownProps;
  let data = {};
  if (shouldFetchOnResource === true) {
    if (resourceId !== null && resourceType !== null) {
      let dataId = `${resourceType}_${resourceId}`;
      if (probe[dataId] && fileNode && probe[dataId].files[fileNode.id]) {
        data = probe[dataId].files[fileNode.id];
      }
    }
  } else {
    if (probe[selectedCredentialId] && fileNode && probe[selectedCredentialId].files[fileNode.id]) {
      data = probe[selectedCredentialId].files[fileNode.id];
    }
  }

  return {
    data,
    open: !!fileNode,
    isLoading: request.PROBE_GET_FILE_REQUEST || false,
  };
};

export default connect(mapStateToProps, {
  probeGetFile,
  probeClearFiles,
})(FilePreview);
